import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { message, Row, Col, Card, Grid, Switch, Tag, Tooltip } from "antd";

import {
  Table,
  Button,
  Input,
  Typography,
  Modal,
  Space,
  Spin,
  Pagination,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  SearchOutlined,
  SafetyCertificateOutlined,
  UndoOutlined,
} from "@ant-design/icons";

import { fetchCustomers, deleteCustomer, restoreCustomer } from "../api";
import { useDeleteConfirm } from "../../../components/DeleteConfirm";
import { usePermissions } from "../../../hooks/usePermissions";
import { ActionType, ModuleType, PermissionButton } from "components";
import "bootstrap/dist/css/bootstrap.min.css";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

function CustomerList() {
  const module = ModuleType.CUSTOMER;
  const navigate = useNavigate();
  const { canViewModule, canViewField, canEditField } = usePermissions();
  const [messageApi, contextHolder] = message.useMessage();
  const screens = useBreakpoint();
  const { confirmDelete, confirmPermanentDelete, confirmRestore, contextHolder: deleteModalHolder, isAdmin } = useDeleteConfirm();
  const [showDeleted, setShowDeleted] = useState(false); // admin: list soft-deleted customers

  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
  });

  // Load data on mount or when search/pagination changes
  useEffect(() => {
    if (canViewModule(ModuleType.CUSTOMER)) {
      loadCustomers();
    }
  }, [searchQuery, pagination.page, pagination.pageSize, showDeleted]);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const res = await fetchCustomers({
        search: searchQuery,
        page: pagination.page,
        limit: pagination.pageSize,
        deleted: showDeleted,
      });
      setCustomers(res.data);
      setPagination((prev) => ({
        ...prev,
        total: res?.meta?.total || 0,
      }));
    } catch (err) {
      console.error("Failed to fetch customers", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCustomer = async (id, permanent) => {
    try {
      await deleteCustomer(id, { permanent });
      loadCustomers(); // refresh table
      messageApi.success(permanent ? "Customer permanently deleted" : "Customer deleted");
    } catch (error) {
      console.error("Delete error:", error);
      messageApi.error(error?.response?.data?.message || "Failed to delete customer");
    }
  };

  const handleRestoreCustomer = async (id) => {
    try {
      await restoreCustomer(id);
      loadCustomers();
      messageApi.success("Customer restored");
    } catch (error) {
      messageApi.error(error?.response?.data?.message || "Failed to restore customer");
    }
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSearchQuery(searchTerm.trim());
  };

  const handleAddCustomer = () => setOpenDialog(true);
  const handleSelectManual = () => {
    setOpenDialog(false);
    navigate("/customers/addCustomer?type=manual");
  };
  const handleSelectDigilocker = () => {
    setOpenDialog(false);
    navigate("/customers/addCustomer?type=digilocker");
  };

  const handlePageChange = (page, pageSize) => {
    setPagination({ ...pagination, page, pageSize });
  };

  const columns = [
    {
      title: "Customer ID",
      dataIndex: "customerId",
      responsive: ['sm', 'md', 'lg'],
      render: (text) => canViewField(module, 'customerId') ? (text || "-") : '***',
      width: 120,
    },
    {
      title: "Name",
      dataIndex: "name",
      responsive: ['xs', 'sm', 'md', 'lg'],
      render: (text, record) => (
        <>
          {canViewField(module, 'name') ? text : '***'}
          {record.isDeleted && (
            <Tooltip title={`Deleted${record.deletedBy?.name ? ` by ${record.deletedBy.name}` : ''}${record.deletedAt ? ` on ${new Date(record.deletedAt).toLocaleString('en-IN')}` : ''}`}>
              <Tag color="red" style={{ marginLeft: 8 }}>DELETED</Tag>
            </Tooltip>
          )}
        </>
      ),
    },
    {
      title: "Phone", 
      dataIndex: "phone",
      responsive: ['sm', 'md', 'lg'],
      render: (text) => canViewField(module, 'phone') ? text : '***',
    },
    {
      title: "Email",
      dataIndex: "email",
      render: (text) => canViewField(module, 'email') ? (text || "-") : '***',
      responsive: ['md', 'lg'],
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      responsive: ['sm', 'md', 'lg'],
      render: (val) => val ? new Date(val).toLocaleDateString('en-IN') : "-",
      width: 120,
    },
    {
      title: "Actions",
      key: "actions",
      responsive: ['xs', 'sm', 'md', 'lg'],
      render: (_, record) => (
        <Space size="small" wrap>
          <PermissionButton
            buttonType={ActionType.VIEW}
            module={module}
            icon={<EyeOutlined />}
            size="small"
            onClick={() => navigate(`/customers/${record._id}`)}
            aria-label="View Customer"
          />
          {record.isDeleted && isAdmin && (
            <>
              <Button
                size="small"
                icon={<UndoOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  confirmRestore({ what: `customer ${record.name}`, onRestore: () => handleRestoreCustomer(record._id) });
                }}
              >
                Restore
              </Button>
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  confirmPermanentDelete({ what: `customer ${record.name}`, onDelete: () => handleDeleteCustomer(record._id, true) });
                }}
              >
                Delete permanently
              </Button>
            </>
          )}
          {!record.isDeleted && canEditField(module, 'name') && (
            <PermissionButton
              buttonType={ActionType.EDIT}
              module={module}
              icon={<EditOutlined />}
              size="small"
              onClick={() => navigate(`/customers/edit/${record._id}`)}
              aria-label="Edit Customer"
            />
          )}
          {!record.isDeleted && canEditField(module, 'delete') && (
            <PermissionButton
              buttonType={ActionType.DELETE}
              module={module}
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                confirmDelete({
                  what: `customer ${record.name}`,
                  onDelete: (permanent) => handleDeleteCustomer(record._id, permanent),
                });
              }}
              aria-label="Delete Customer"
            />
          )}
        </Space>
      ),
    },
  ];

  if (!canViewModule(ModuleType.CUSTOMER)) {
    return (
      <div className="text-center mt-5">
        <Text type="danger">You do not have permission to view customers.</Text>
      </div>
    );
  }

  return (
    <>
      {contextHolder}
      {deleteModalHolder}
      <main className="container-fluid px-2 px-md-4 py-3">
        <Card
          bordered={false}
          style={{
            borderRadius: 12,
            boxShadow: "0 2px 12px rgba(22,119,255,0.06)",
          }}
          bodyStyle={{ padding: screens.xs ? 12 : 24 }}
        >
          <Row justify="space-between" align="middle" className="mb-3" gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Title level={4} className="mb-0">Customers{showDeleted && <Text type="danger"> — Deleted</Text>}</Title>
            </Col>
            <Col xs={24} md={16} className="d-flex flex-wrap justify-content-md-end gap-2">
              <Input
                placeholder="Search by customer ID, name, phone, Aadhaar, PAN"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onPressEnter={handleSearch}
                prefix={<SearchOutlined />}
                style={{ 
                  width: screens.xs ? '100%' : 300,
                  marginBottom: screens.xs ? 8 : 0 
                }}
                aria-label="Search Customers"
              />
              <Button onClick={handleSearch} className="me-2">Search</Button>
              {isAdmin && (
                <Space size={4} className="me-2">
                  <Switch
                    size="small"
                    checked={showDeleted}
                    onChange={(v) => { setShowDeleted(v); setPagination((prev) => ({ ...prev, page: 1 })); }}
                  />
                  <Text type={showDeleted ? 'danger' : 'secondary'}>Show deleted</Text>
                </Space>
              )}
              <PermissionButton
                buttonType={ActionType.CREATE}
                module={module}
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddCustomer}
                aria-label="Add Customer"
              >
                Add Customer
              </PermissionButton>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-5">
              <Spin size="large" />
            </div>
          ) : (
            <>
              <Table
                rowKey="_id"
                columns={columns}
                dataSource={customers}
                pagination={false}
                scroll={{ x: true }}
                size={screens.xs ? "small" : "middle"}
                onRow={(record) => ({
                  onClick: () => navigate(`/customers/${record._id}`),
                  style: { cursor: 'pointer' },
                })}
                locale={{ emptyText: 'No customers found.' }}
              />
              <Row justify="end" className="mt-3">
                <Col>
                  <Pagination
                    current={pagination.page}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    onChange={handlePageChange}
                    showSizeChanger
                    pageSizeOptions={["5", "10", "25"]}
                    aria-label="Pagination"
                  />
                </Col>
              </Row>
            </>
          )}
        </Card>

        <Modal
          title={
            <div className="text-center pt-3 pb-2">
              <Title level={4} style={{ marginBottom: 8, color: '#1f2937' }}>Add New Customer</Title>
              <Text type="secondary">Choose a method to securely add a customer</Text>
            </div>
          }
          open={openDialog}
          onCancel={() => setOpenDialog(false)}
          footer={null}
          centered
          width={screens.xs ? "95vw" : 450}
          bodyStyle={{ padding: '8px 24px 24px' }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Card
              hoverable
              onClick={handleSelectDigilocker}
              style={{ borderColor: '#1677ff', backgroundColor: '#f0f7ff', borderRadius: '12px', overflow: 'hidden' }}
              bodyStyle={{ padding: '16px 20px' }}
            >
              <Row align="middle" wrap={false} gutter={16}>
                <Col>
                  <div style={{ backgroundColor: '#e6f4ff', padding: '12px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <SafetyCertificateOutlined style={{ fontSize: '24px', color: '#1677ff' }} />
                  </div>
                </Col>
                <Col flex="auto">
                  <div style={{ fontWeight: 600, fontSize: '16px', color: '#1677ff', marginBottom: '4px' }}>Fetch from DigiLocker</div>
                  <div style={{ fontSize: '13px', color: '#595959', lineHeight: '1.4' }}>Quick, secure, and paperless KYC (Recommended)</div>
                </Col>
              </Row>
            </Card>
            
            <Card
              hoverable
              onClick={handleSelectManual}
              style={{ borderRadius: '12px', overflow: 'hidden', borderColor: '#d9d9d9' }}
              bodyStyle={{ padding: '16px 20px' }}
            >
              <Row align="middle" wrap={false} gutter={16}>
                <Col>
                  <div style={{ backgroundColor: '#fff7e6', padding: '12px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <EditOutlined style={{ fontSize: '24px', color: '#fa8c16' }} />
                  </div>
                </Col>
                <Col flex="auto">
                  <div style={{ fontWeight: 600, fontSize: '16px', color: '#262626', marginBottom: '4px' }}>Manual Entry</div>
                  <div style={{ fontSize: '13px', color: '#595959', lineHeight: '1.4' }}>Enter customer details manually without auto-fetch</div>
                </Col>
              </Row>
            </Card>
          </div>
        </Modal>
      </main>
    </>
  );
}

export default CustomerList;
